import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getGroups, deleteGroup } from '../services/iam';
import { Group } from '../types/iam';
import GroupFormModal from '../components/modals/GroupFormModal';
import ManageGroupMembersModal from '../components/modals/ManageGroupMembersModal';
import { toast } from '@/lib/toast';
import { AlertTriangle, Group as GroupIcon, Pencil, Plus, RefreshCw, SearchX, Trash2, UsersRound } from 'lucide-react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import PermissionGate from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { Button, Card, EmptyState, IconButton, PageHeader, PaginationBar, SearchField, TanstackTable } from '@/components/ds-zta';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { useUrlState } from '@/lib/useUrlState';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { ROWS_PER_PAGE_OPTIONS, pageOffset, readPage, readRows } from '@/lib/urlPaging';

const columnHelper = createColumnHelper<Group>();

const GroupsPage: React.FC = () => {
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [isMembersModalOpen, setMembersModalOpen] = useState<Group | null>(null);
  // Search and paging live in the URL (design eval F24); the search goes to
  // the server as `q` (F7), and a new search starts again at page 1.
  const [url, setUrl] = useUrlState({ q: '', page: '1', rows: '20' });
  const page = readPage(url.page);
  const itemsPerPage = readRows(url.rows);
  const q = useDebouncedValue(url.q.trim());

  const queryClient = useQueryClient();
  const { PERMISSIONS } = usePermissions();
  const { confirm: confirmDialog } = useDialogs();

  const { data: groupsResponse, isLoading, isError, error, refetch } = useQuery(
    ['groups', q, page, itemsPerPage],
    async () => {
      const response = await getGroups({ q: q || undefined, limit: itemsPerPage, offset: pageOffset(page, itemsPerPage) });
      return response;
    },
    { keepPreviousData: true }
  );

  const deleteMutation = useMutation(deleteGroup, {
    onSuccess: () => {
      queryClient.invalidateQueries('groups');
      toast.success('Group deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete group: ${error.message}`);
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        meta: { sortable: true, sortValue: (g) => (g.name || '').toLowerCase() },
        cell: (info) => {
          const group = info.row.original;
          return (
            <Link 
              to={`/groups/${group.id}`} 
              className="text-dense font-medium text-[var(--d2d-text-primary)] hover:underline"
            >
              {info.getValue()}
            </Link>
          );
        },
      }),
      columnHelper.accessor('key', {
        header: 'Key',
        meta: { sortable: true },
        cell: (info) => (
          <span className="text-dense text-[var(--d2d-text-muted)]">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (info) => (
          <span className="text-dense text-[var(--d2d-text-muted)]">
            {info.getValue()}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => <span className="block text-right">Actions</span>,
        cell: (info) => {
          const group = info.row.original;
          return (
            <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
              <PermissionGate permission={PERMISSIONS.GROUPS_UPDATE}>
                <IconButton
                  size="sm"
                  aria-label="Edit group"
                  tooltip="Edit group"
                  icon={<Pencil className="h-4 w-4" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingGroup(group);
                  }}
                />
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.GROUPS_MANAGE_MEMBERS}>
                <IconButton
                  size="sm"
                  aria-label="Manage members"
                  tooltip="Manage members"
                  icon={<UsersRound className="h-4 w-4" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMembersModalOpen(group);
                  }}
                />
              </PermissionGate>
              <PermissionGate permission={PERMISSIONS.GROUPS_DELETE}>
                <IconButton
                  size="sm"
                  aria-label="Delete group"
                  tooltip="Delete group"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (await confirmDialog({
                      title: `Delete ${group.name}?`,
                      message: `"${group.name}" will be permanently deleted. Its members lose any access granted through this group.`,
                      confirmLabel: 'Delete',
                      variant: 'danger',
                    })) {
                      deleteMutation.mutate(group.id);
                    }
                  }}
                  disabled={deleteMutation.isLoading}
                />
              </PermissionGate>
            </div>
          );
        },
      }),
    ],
    [deleteMutation.isLoading, confirmDialog]
  );

  const groups = groupsResponse?.data || [];
  const total = groupsResponse?.total || 0;

  const table = useReactTable({
    data: groups,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePageChange = (newPage: number) => {
    setUrl({ page: newPage });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setUrl({ rows: newItemsPerPage, page: null });
  };

  const openCreate = () => setCreateModalOpen(true);

  const emptyState = isError ? (
    <EmptyState
      icon={<AlertTriangle />}
      title="Couldn't load groups"
      description={
        (error as any)?.response?.data?.error ||
        (error as any)?.response?.data?.message ||
        (error as any)?.message ||
        'Something went wrong while loading this list.'
      }
      action={
        <Button size="sm" variant="secondary" leadingIcon={<RefreshCw className="h-4 w-4" />} onClick={() => refetch()}>
          Retry
        </Button>
      }
    />
  ) : q ? (
    <EmptyState
      icon={<SearchX />}
      title={`No groups match "${q}"`}
      description="Try a different search term."
      action={
        <Button size="sm" variant="secondary" onClick={() => setUrl({ q: null, page: null })}>
          Clear search
        </Button>
      }
    />
  ) : (
    <EmptyState
      icon={<GroupIcon />}
      title="No groups yet"
      description="Groups let you grant roles to many users at once. Create one, then add members."
      action={
        <PermissionGate permission={PERMISSIONS.GROUPS_CREATE}>
          <Button size="sm" leadingIcon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={openCreate}>
            Create Group
          </Button>
        </PermissionGate>
      }
    />
  );

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        icon={<GroupIcon />}
        title="Group Management"
        description="Organize users into groups for easier permission management."
        primaryAction={
          <PermissionGate permission={PERMISSIONS.GROUPS_CREATE}>
            <Button leadingIcon={<Plus className="h-4 w-4" aria-hidden="true" />} onClick={openCreate}>
              Create Group
            </Button>
          </PermissionGate>
        }
      />
      <div className="max-w-md">
        <SearchField
          label="Search groups"
          placeholder="Search groups…"
          value={url.q}
          onChange={(next) => setUrl({ q: next, page: null }, { replace: true })}
        />
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <TanstackTable aria-label="Groups" table={table} loading={isLoading} empty={emptyState} />
        </div>
      </Card>
        {total > 0 && (
          <div>
          {<PaginationBar
            page={page}
            totalRows={total}
            rowsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleItemsPerPageChange}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />}
          </div>
        )}

      {isCreateModalOpen && (
        <GroupFormModal 
          onClose={() => setCreateModalOpen(false)}
        />
      )}

      {editingGroup && (
        <GroupFormModal 
          group={editingGroup}
          onClose={() => setEditingGroup(null)}
        />
      )}

      {isMembersModalOpen && (
        <ManageGroupMembersModal 
          groupId={isMembersModalOpen.id}
          groupName={isMembersModalOpen.name}
          onClose={() => setMembersModalOpen(null)}
        />
      )}
    </div>
  );
};

export default GroupsPage;
